import React, { useRef, useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Progress } from 'semantic-ui-react';
import Step from './Step';
import { StepsContext } from '..';
import CustomIcon from '~/components/CustomIcon';

const ScrollContainer = styled.div<{$showLeftPadding: boolean}>`
  display: flex;
  overflow-x: auto;
  padding: 8px ${props => props.$showLeftPadding ? '40px' : '110px'};
  gap: 8px;
  height: 110px;
  max-width: calc(100% - 100px);
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  border-radius: 50%;
  width: 37px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: none;
  top: 40px;
`;

const StyledProgress = styled(Progress)`
  .bar {
    min-width: 0px !important;
  }
`

const StepJourneyTabs:React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    steps,
    hansleStepClick,
    activeTab,
    isLoadingResults,
    breed,
    lastStepIndex
  } = useContext(StepsContext)

  const activeTabRef = useRef(activeTab)

  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [progress, setProgress] = useState(0)
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [previousActiveTab, setPreviousActiveTab] = useState(0)

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
  
    checkScrollability();
  
    window.addEventListener('resize', checkScrollability);
    scrollContainer?.addEventListener('scroll', checkScrollability);
    scrollContainerRef.current.addEventListener('scrollend', handleScrollEnd);
    
    const targetStep = scrollContainer?.querySelector(`[data-step="${1}"]`);
    if (targetStep) {
      targetStep.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScrollability);
      scrollContainer?.removeEventListener('scroll', checkScrollability);
      scrollContainer?.removeEventListener('scroll', handleScrollEnd);
    };
  }, [steps, activeTab]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [steps, activeTab]);

  const handleScrollEnd = () => {
    const activeTab = activeTabRef.current || 0
    if (scrollContainerRef.current && activeTab <= steps.length - 1) {
      const container = scrollContainerRef.current;
      const width = container.offsetWidth;
      const children = Array.from(container.children);
      const targetEl = children[activeTab + 1] as HTMLElement | undefined;
      const containerRect = container.getBoundingClientRect();

      const indexFirstInView = children.findIndex((step, index) => {
        if(index !== 0) {
          const targetRect = step.getBoundingClientRect();
          return targetRect.left >= containerRect.left && targetRect.right <= containerRect.right
        }
      })

      const indexLastNotInView = children.findIndex((step, index) => {
        if(index !== 0 && index >= indexFirstInView) {
          const targetRect = step.getBoundingClientRect();
          const isInView =
          targetRect.left >= containerRect.left &&
          targetRect.right <= containerRect.right;
          return !isInView
        }
      }) || children.length - 1

      if (targetEl) {
        const targetRect = targetEl.getBoundingClientRect();
        const distanceBetweenSteps = activeTab + 1 > 3 ? 320 : 280
        const distanceStep = targetRect.x - distanceBetweenSteps;
        const percent = Math.floor((distanceStep / width) * 100);
  
        const isInView =
          targetRect.left >= containerRect.left &&
          targetRect.right <= containerRect.right;

        if(!isInView) {
          //returning from left and current step is bigger then all rendered steps
          if(indexLastNotInView >= 0 && activeTab > indexLastNotInView - 1) {
            setProgress(100)
          } else {
            setProgress(0);
          }
          return
        }
        setProgress(percent);
      }
    }
  }

  useEffect(() => {
    if (scrollContainerRef.current && activeTab <= steps.length - 1) {
      const container = scrollContainerRef.current;
      const width = container.offsetWidth;
      const children = Array.from(container.children);
      const targetEl = children[activeTab + 1] as HTMLElement | undefined;
  
      if (targetEl) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const distanceBetweenSteps = activeTab + 1 > 3 ? 320 : 280
        const distanceStep = targetRect.x - distanceBetweenSteps;
        const percent = Math.floor((distanceStep / width) * 100);
  
        const isInView =
          targetRect.left >= containerRect.left &&
          targetRect.right <= containerRect.right;

        if(!isInView) {
          if(activeTab > previousActiveTab) {
            handleScroll('right')
          } else {
            handleScroll('left')
          }
          setProgress(0);
          return
        }
        setProgress(percent);
      }
    }
    setPreviousActiveTab(activeTab)
  }, [steps, activeTab]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const { clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth;
      /*  if(direction === 'right') {
        setProgress(0)
      } */
      scrollContainerRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', padding: '0px 40px' }}>
      {showLeftScroll && (
        <ArrowWrapper onClick={() => handleScroll('left')} style={{ left: 0 }}>
          <CustomIcon name='chevron circle left' style={{ fontSize: '2.3em' }} />
        </ArrowWrapper>
      )}
      <ScrollContainer
        $showLeftPadding={!showLeftScroll}
        ref={scrollContainerRef}
      >
        <StyledProgress
          percent={progress}
          size='tiny'
          active
          style={{
            position: 'absolute',
            width: 'calc(100% - 100px)',
            top: 30,
            right: 50,
            left: 50
          }}
        />
        {steps.map((stepItem, index) => (
          <div key={stepItem.id}>
            <Step
              step={stepItem}
              index={index}
              handleStepClick={() => hansleStepClick(index)}
              active={activeTab === index || activeTab > index}
              isLoading={activeTab === index && isLoadingResults}
              isLoadingResults={isLoadingResults}
              breed={breed}
              lastStepIndex={lastStepIndex}
            />
          </div>
        ))}
      </ScrollContainer>
      {showRightScroll && (
        <ArrowWrapper onClick={() => handleScroll('right')} style={{ right: 0 }}>
          <CustomIcon name='chevron circle right' style={{ fontSize: '2.3em' }} />
        </ArrowWrapper>
      )}
    </div>
  );
};

export default StepJourneyTabs;